/** @jsxImportSource ./simboluri-jsx */
import { useState } from "react";
import { CazLegenda, Combinatie, CombinatiiResult, StepState, fmtDate } from "./tipuri";
import ModeleRigiditate from "./ModeleRigiditate";
import Info from "./Info";
import { DescriereaPasului } from "./TitluPas";
import Marime from "./Marime";
import Legenda from "./LegendaCazuri";

import NotePas from "./NotePas";
import { numar, t, tn, tx, useT, type Cheie } from "../i18n";
import { traduceRezultat } from "./texteRezultat";
// Pasul 11 — Combinațiile de încărcări: driftul și coeficientul de
// sensibilitate θ (efectele de ordinul II) per nivel și direcție, factorul
// de amplificare α = 1/(1-θ), apoi matricea combinațiilor CR 0-2012
// (fundamentale + seismice) aplicată pe tensorul de eforturi.

// ordinea canonică a coloanelor din matricea combinațiilor (aceeași cu
// ordinea cazurilor din tensorul Pașilor 8–9)
const ORDINE_CAZURI = [
  "G1", "G4", "G2c", "G2a", "G2b", "G3", "Q1", "Q2", "S",
  "E_0", "E_45", "E_90", "E_135", "T_accX", "T_accY",
];

// Ce înseamnă fiecare simbol — varianta „de rezervă", folosită la rezultatele
// vechi, rulate înainte ca Pasul 11 să întoarcă legenda cu valorile reale.
// Textele lungi (cu cifrele proiectului) vin din backend: `legenda_cazuri`.
const CAZ_FALLBACK: Record<string, { nume: Cheie; tip: string }> = {
  G1: { nume: "struct.combina.caz_g1", tip: "permanenta" },
  G4: { nume: "struct.combina.caz_g4", tip: "permanenta" },
  G2a: { nume: "struct.combina.peretii_nestructurali_au", tip: "permanenta" },
  G2b: { nume: "struct.combina.peretii_nestructurali_fa", tip: "permanenta" },
  G2c: { nume: "struct.combina.peretii_parterului_fara", tip: "permanenta" },
  G3: { nume: "struct.combina.finisajele_sapa_pardosea", tip: "permanenta" },
  Q1: { nume: "struct.combina.utila_a_spatii", tip: "variabila" },
  Q2: { nume: "struct.combina.utila_c_circulatii", tip: "variabila" },
  S: { nume: "struct.combina.zapada_acoperis", tip: "variabila" },
  E_0: { nume: "struct.combina.cutremurul_directia_0", tip: "seismica" },
  E_45: { nume: "struct.combina.cutremurul_directia_45", tip: "seismica" },
  E_90: { nume: "struct.combina.cutremurul_directia_90", tip: "seismica" },
  E_135: { nume: "struct.combina.cutremurul_directia_135", tip: "seismica" },
  T_accX: { nume: "struct.combina.torsiunea_accidentala_cu", tip: "torsiune" },
  T_accY: { nume: "struct.combina.torsiunea_accidentala_cu2", tip: "torsiune" },
};

// „1,35" în loc de „1.35" (în română; în engleză, cu punct)
function nr(v: number | undefined, zecimale = 2): string {
  if (v === undefined || v === null) return "—";
  return numar(v, { minimumFractionDigits: zecimale, maximumFractionDigits: zecimale,
                    useGrouping: false });
}

function kN(v: number | undefined): string {
  if (v === undefined || v === null) return "—";
  return numar(Math.round(v));
}

export default function StepCombinatii({
  state,
  running,
}: {
  state: StepState | null;
  running: boolean;
}) {
  useT();
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.combina.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.combina.genereaza_combinatiile_c")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.combina.pasul_nu_rulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="combinatii" />
      </div>
    );
  }

  const r = state.result as unknown as CombinatiiResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.combina.ultima_rulare_esuat", {
            motiv: r?.error ?? t("struct.combina.eroare_necunoscuta"),
          })}
        </div>
        <div className="sg-muted">{t("struct.combina.ruleaza_nou_meniul")}</div>
      </div>
    );
  }

  return (
    <div className="st-result">
      <ModeleRigiditate pas={11} r={r} />
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">{t("struct.combina.combinatii_generate")}<Marime cheie="nr_combinatii" /></div>
          <div className="st-card-value">{r.nr_combinatii}</div>
          <div className="st-card-sub">
            {t("struct.combina.card_sub_fund_seism", {
              f: r.nr_fundamentale, s: r.nr_seismice,
              gs: (r.nr_gs ?? 0) > 0
                ? t("struct.combina.card_sub_gs", { n: r.nr_gs }) : "",
              set: r.set_combinatii
                ? t("struct.combina.card_sub_set", { nume: traduceRezultat(r.set_combinatii.nume) }) : "",
            })}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">{t("struct.combina.facute")}</div>
          <div className="st-card-sub" style={{ marginTop: 6, lineHeight: 1.5 }}>
            {r.set_combinatii ? (
              <>
                {r.set_combinatii.de_ce}
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {r.set_combinatii.compozitie.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                {tx("struct.combina.compozitie_implicita")}<br />
                {tx("struct.combina.compozitie_seismice")}<br />
                {tx("struct.combina.compozitie_gs")}
              </>
            )}
          </div>
        </div>
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.combina.alpha_amplificarea")}
            <Info
              titlu={t("struct.combina.1_1")}
              ce={t("struct.combina.p_d_r")}
              deCe={t("struct.combina.cladire_deplasata_latera")}
              parametri={[
                { simbol: "P", nume: t("struct.combina.greutatea_totala_deasupr"), sursa: t("struct.combina.masele_pasului_7") },
                { simbol: "d_r", nume: t("struct.combina.deplasarea_relativa_nive"), sursa: t("struct.combina.deplasarile_cazurilor_0") },
                { simbol: "V", nume: t("struct.combina.forta_taietoare_a"), sursa: t("struct.combina.tensorul_de_eforturi") },
              ]}
            />
          </div>
          <div className="st-card-value">
            {r.alpha.X} / {r.alpha.Y}
          </div>
          <div className="st-card-sub">{t("struct.combina.alpha_sub")}</div>
        </div>
      </div>

      {r.set_combinatii && r.set_combinatii.limitari.length > 0 && (
        <div className="st-findings">
          <h3>
            {t("struct.combina.ce_nu_acopera")}
            <span className="sg-muted" style={{ fontWeight: 400 }}>
              {" "}{t("struct.combina.limitari_cunoscute", {
                n: r.set_combinatii.limitari.length,
              })}
            </span>
          </h3>
          <p className="sg-muted" style={{ maxWidth: 860 }}>
            {t("struct.combina.setul_redus_alegere")}
          </p>
          {r.set_combinatii.limitari.map((l) => (
            <div key={l.cod} className="st-warn" style={{ display: "block", marginBottom: 10 }}>
              <div>
                <span className="sg-mono">{l.cod}</span>{" "}
                <strong>{l.titlu}</strong>
              </div>
              <div className="sg-muted" style={{ marginTop: 4 }}>{l.ce}</div>
              <div className="sg-muted" style={{ marginTop: 4 }}>
                <strong>{t("struct.combina.normativul_eticheta")}</strong> {l.normativ}
              </div>
              <div className="sg-muted" style={{ marginTop: 4 }}>
                <strong>{t("struct.combina.efectul_eticheta")}</strong> {l.efect}
              </div>
              <div className="sg-muted" style={{ marginTop: 4 }}>
                <strong>{t("struct.combina.acopera")}</strong> {l.acoperit_de}
              </div>
            </div>
          ))}
          <p className="sg-muted" style={{ marginTop: 6 }}>
            {tx("struct.combina.cum_se_schimba", {
              cum: r.set_combinatii.cum_se_schimba,
            })}
          </p>
        </div>
      )}

      <NotePas note={r.avertismente} titlu="struct.note.combinatii" />

      {r.theta_sumar && r.theta_sumar.length > 0 && (
        <div className="st-findings">
          <h3>
            {t("struct.combina.alpha_vine_din_10")}
            <span className="sg-muted" style={{ fontWeight: 400 }}>
              {" "}{t("struct.combina.deplasari_laterale")}
            </span>
          </h3>
          <p className="sg-muted" style={{ maxWidth: 820 }}>
            {tx("struct.combina.driftul_s_a_mutat")}
            {r.sursa_alpha && t("struct.combina.sursa_alpha", { sursa: r.sursa_alpha })}
          </p>
          <table className="sg-table">
            <thead>
              <tr><th>{t("struct.combina.directia")}</th><th>θ_max</th>
                  <th>{t("struct.combina.th_alpha_aplicat")}</th></tr>
            </thead>
            <tbody>
              {r.theta_sumar.map((s) => (
                <tr key={s.directie}>
                  <td>{s.directie}</td>
                  <td className="sg-mono">{s.theta_max}</td>
                  <td className="sg-mono">{s.alpha}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LegendaCazuri r={r} />

      <CoeficientiCombinatii r={r} />

      <MatriceCombinatii r={r} />

      <div className="sg-muted">{r.nota}</div>
      <div className="sg-muted st-ran-at">{t("struct.pas.rulat_la", { cand: fmtDate(state.updated_at) })}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drifturile + θ per nivel, pe o direcție
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Legenda cazurilor de încărcare: ce înseamnă G1, G2a, …, E_0, T_accX
// ---------------------------------------------------------------------------

// cazurile care apar efectiv în matrice, în ordinea canonică
function cazuriDinMatrice(r: CombinatiiResult): string[] {
  const cazuri = ORDINE_CAZURI.filter((c) =>
    r.combinatii.some((cb) => c in cb.coeficienti)
  );
  r.combinatii.forEach((cb) => {
    Object.keys(cb.coeficienti).forEach((c) => {
      if (!cazuri.includes(c)) cazuri.push(c);
    });
  });
  return cazuri;
}

// legenda din backend; la rezultatele vechi — varianta scurtă, fără cifre
function legendaCazuri(r: CombinatiiResult): CazLegenda[] {
  if (r.legenda_cazuri && r.legenda_cazuri.length > 0) return r.legenda_cazuri;
  return cazuriDinMatrice(r).map((c) => ({
    caz: c,
    nume: CAZ_FALLBACK[c] ? t(CAZ_FALLBACK[c].nume) : c,
    tip: CAZ_FALLBACK[c]?.tip ?? "necunoscut",
  }));
}

// Cum intră cazul în combinații — coeficienții lui, cu cifrele proiectului.
function rolInCombinatii(l: CazLegenda, r: CombinatiiResult): string {
  const p = r.parametri_combinare;
  const gQ = p?.gamma_q ?? 1.5;
  if (l.tip === "permanenta") {
    return t("struct.combina.rol_permanenta", {
      gd: nr(p?.gamma_g_defavorabil ?? 1.35),
      gf: nr(p?.gamma_g_favorabil ?? 1.0),
    });
  }
  if (l.tip === "variabila") {
    const psi0 = l.psi0;
    const psi2 = l.psi2;
    const nedom = psi0 !== undefined ? nr(gQ * psi0) : `${numar(1.5)}·ψ₀`;
    return t("struct.combina.rol_variabila", {
      gq: nr(gQ), nedom, psi0: nr(psi0, 2), psi2: nr(psi2, 2),
    });
  }
  if (l.tip === "seismica") {
    const ax = l.caz === "E_90" ? "Y" : "X";
    const a = ax === "Y" ? r.alpha.Y : r.alpha.X;
    return t("struct.combina.rol_seismica", { a: nr(a, 3) });
  }
  if (l.tip === "torsiune") {
    return t("struct.combina.rol_torsiune");
  }
  return "—";
}

function LegendaCazuri({ r }: { r: CombinatiiResult }) {
  useT();
  const legenda = legendaCazuri(r);
  const areValori = !!(r.legenda_cazuri && r.legenda_cazuri.length > 0);
  return (
    <Legenda
      legenda={legenda}
      rol={(l) => rolInCombinatii(l, r)}
      avertisment={
        areValori ? null : (
          <div style={{ marginTop: 6 }}>
            {t("struct.combina.rezultatul_acesta_a")}
          </div>
        )
      }
      subsol={
        <div className="sg-muted" style={{ marginTop: 8, lineHeight: 1.55 }}>
          <strong>{t("struct.combina.atentie_doua_cazuri")}</strong>{" "}
          {tx("struct.combina.subsol_e_theta", {
            q: r.q ? t("struct.combina.q_egal", { v: nr(r.q, 2) }) : "",
          })}
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// De unde vine fiecare coeficient din matrice
// ---------------------------------------------------------------------------

function CoeficientiCombinatii({ r }: { r: CombinatiiResult }) {
  useT();
  const p = r.parametri_combinare;
  const gQ = p?.gamma_q ?? 1.5;
  const psi0u = p?.psi0_util;
  const psi0z = p?.psi0_zapada;
  const rinduri: { simbol: string; valoare: string; ce: string; sursa: string }[] = [
    {
      simbol: t("struct.combina.simbol_gg_defav"),
      valoare: nr(p?.gamma_g_defavorabil ?? 1.35),
      ce: t("struct.combina.ce_gg_defav"),
      sursa: t("struct.combina.sursa_gg_defav"),
    },
    {
      simbol: t("struct.combina.simbol_gg_fav"),
      valoare: nr(p?.gamma_g_favorabil ?? 1.0),
      ce: t("struct.combina.ce_gg_fav"),
      sursa: t("struct.combina.cr_0_20122"),
    },
    {
      simbol: t("struct.combina.simbol_gq_dom"),
      valoare: nr(gQ),
      ce: t("struct.combina.ce_gq_dom"),
      sursa: "CR 0-2012 §6.3, rel. (6.7)",
    },
    {
      simbol: t("struct.combina.simbol_gq_nedom"),
      valoare: psi0u !== undefined
        ? t("struct.combina.valoare_psi0", {
            gq: nr(gQ), psi0u: nr(psi0u, 2), u: nr(gQ * psi0u),
            psi0z: nr(psi0z ?? 0, 2), z: nr(gQ * (psi0z ?? 0)),
          })
        : `${numar(1.5)}·ψ₀`,
      ce: t("struct.combina.ce_gq_nedom"),
      sursa: t("struct.combina.0_cr_0"),
    },
    {
      simbol: t("struct.combina.simbol_psi2"),
      valoare: p
        ? t("struct.combina.valoare_psi2", {
            q1: nr(p.psi2_util_a, 2), q2: nr(p.psi2_util_c, 2),
            s: nr(p.psi2_zapada, 2),
          })
        : "—",
      ce: t("struct.combina.ce_psi2"),
      sursa: "CR 0-2012 tab. 7.1 + P100-1/2013 §3.3.2 (rel. 3.7)",
    },
    {
      simbol: "α = 1/(1−θ)",
      valoare: t("struct.combina.valoare_alpha_xy", {
        x: nr(r.alpha.X, 3), y: nr(r.alpha.Y, 3),
      }),
      ce: t("struct.combina.ce_alpha"),
      sursa: t("struct.combina.p100_1_2013"),
    },
    {
      simbol: t("struct.combina.simbol_regula_30"),
      valoare: nr(p?.regula_30 ?? 0.3, 2),
      ce: t("struct.combina.ce_regula_30"),
      sursa: "P100-1/2013 §4.5.3.6.1 (rel. 4.16–4.17)",
    },
    {
      simbol: t("struct.combina.simbol_semne"),
      valoare: "+1 / −1",
      ce: t("struct.combina.ce_semne", { n: r.nr_seismice }),
      sursa: "P100-1/2013 §4.5.3.6.1",
    },
    {
      simbol: "γ_I,e",
      valoare: p ? nr(p.gamma_ie, 2) : "—",
      ce: t("struct.combina.ce_gamma_ie"),
      sursa: "P100-1/2013 §4.4.5 / tab. 4.2",
    },
  ];

  return (
    <div className="st-findings">
      <h3>
        {t("struct.combina.coeficientii_titlu")}
        <Info
          titlu={t("struct.combina.nicio_cifra_matrice")}
          ce={t("struct.combina.coeficientii_fie_impusi")}
          deCe={t("struct.combina.combinatiile_impreuna_fo")}
        />
      </h3>
      <div style={{ overflowX: "auto" }}>
        <table className="sg-table st-strat">
          <thead>
            <tr>
              <th>{t("struct.combina.th_coeficient")}</th>
              <th>{t("struct.combina.valoarea_proiect")}</th>
              <th>{t("struct.combina.inseamna")}</th>
              <th>{t("struct.combina.input_decizie_sursa")}</th>
            </tr>
          </thead>
          <tbody>
            {rinduri.map((x) => (
              <tr key={x.simbol}>
                <td className="sg-mono" style={{ whiteSpace: "nowrap" }}>
                  <strong>{x.simbol}</strong>
                </td>
                <td className="sg-mono">{x.valoare}</td>
                <td>{x.ce}</td>
                <td className="sg-muted">{x.sursa}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Matricea combinațiilor (colapsabilă): rânduri = combinații, coloane = cazuri
// ---------------------------------------------------------------------------

// Descrierea unei combinații se face din COEFICIENȚII ei, nu din numele ei.
// Motivul: regexurile pe nume erau scrise pentru setul
// COMPLET (`ES30_XPMp`, 4 grupe de semn), iar setul redus — implicitul —
// generează `ES30_XPP`, cu 3. Nicio potrivire ⇒ toate cele 8 combinații
// seismice ale unei rulări normale aveau explicația GOALĂ. Din coeficienți
// descrierea nu se poate desincroniza de matricea afișată alături: sunt
// exact cifrele din tabel.

const SEMN = (v: number) => (v >= 0 ? "+" : "−");

function descrieCombinatie(cb: Combinatie): string {
  const c = cb.coeficienti ?? {};
  const val = (k: string) => c[k];
  const are = (k: string) => c[k] !== undefined && c[k] !== 0;

  if (cb.grupa === "gs") {
    return t("struct.combina.descrie_gs");
  }

  if (cb.grupa === "fundamentala") {
    const gg = val("G1") ?? 1.35;
    const fav = gg > 1.0
      ? t("struct.combina.gg_defavorabil")
      : t("struct.combina.gg_favorabil");
    const dominante = ["Q1", "Q2", "S"].filter((k) => (val(k) ?? 0) >= 1.5);
    const reduse = ["Q1", "Q2", "S"].filter(
      (k) => are(k) && (val(k) as number) < 1.5,
    );
    const dom = dominante.length
      ? t("struct.combina.actiunea_variabila_domin", { p1: dominante.join(" + ") })
      : t("struct.combina.nicio_dominanta");
    const rest = reduse.length
      ? tn("struct.combina.rest_reduse#n", reduse.length, {
          lista: reduse.join(", "),
          val: reduse.map((k) => nr(val(k) as number)).join(", ×"),
        })
      : "";
    return t("struct.combina.descrie_fundamentala", {
      dom, rest, gg: nr(gg), fav,
    });
  }

  if (cb.grupa === "seismica") {
    // direcțiile seismice prezente, în ordinea ponderii
    const dir = Object.keys(c)
      .filter((k) => /^E_\d+$/.test(k) && c[k] !== 0)
      .sort((a, b) => Math.abs(c[b]) - Math.abs(c[a]));
    if (!dir.length) return "";
    const grad = (k: string) => k.slice(2);
    const parti = dir.map((k) => t("struct.combina.parte_seismica", {
      pct: Math.round(Math.abs(c[k]) * 100), grade: grad(k), semn: SEMN(c[k]),
    }));
    const tors = ["T_accX", "T_accY"].filter((k) => are(k));
    const torsTxt = tors.length
      ? tors
          .map((k) => {
            const ax = k.endsWith("X") ? "X" : "Y";
            // axa dominantă a combinației = cea a primului termen seismic
            const dominant = dir[0];
            const acelasiSemn =
              Math.sign(c[k]) === Math.sign(c[dominant]);
            return t("struct.combina.torsiune_pe_axa", {
              ax, semn: SEMN(c[k]),
              relatie: t(acelasiSemn
                ? "struct.combina.acelasi_semn" : "struct.combina.semn_opus"),
            });
          })
          .join("; ") + "."
      : t("struct.combina.fara_torsiune");
    const reg =
      dir.length > 1
        ? t("struct.combina.regula_30_prefix")
        : t("struct.combina.o_singura_directie");
    return t("struct.combina.descrie_seismica", {
      reg, parti: parti.join(t("struct.combina.simultan_cu")), tors: torsTxt,
    });
  }

  return "";
}

// Rolul grupei în înfășurătoare: CHEI, nu texte — grupa vine de la server cu
// codul ei, iar explicația e a interfeței.
const DESCRIERE_GRUPA: Record<string, Cheie> = {
  fundamentala: "struct.combina.rol_fundamentala",
  seismica: "struct.combina.rol_seismica_grupa",
  gs: "struct.combina.rol_gs",
};

function MatriceCombinatii({ r }: { r: CombinatiiResult }) {
  useT();
  const [deschis, setDeschis] = useState(false);

  // coloanele: ordinea canonică + orice caz suplimentar întâlnit
  const cazuri = cazuriDinMatrice(r);
  const legenda = new Map(legendaCazuri(r).map((l) => [l.caz, l]));

  return (
    <div className="st-findings">
      <h3>
        {t("struct.combina.matricea_combinatiilor")}
        <Info
          titlu={t("struct.combina.rand_combinatie_coloana")}
          ce={t("struct.combina.combinatie_lista_coefici")}
          deCe={t("struct.combina.gruparea_fundamentala_cr")}
        />
        <button
          className="md-tab"
          style={{ marginLeft: 10 }}
          onClick={() => setDeschis(!deschis)}
        >
          {deschis
            ? t("struct.combina.ascunde")
            : t("struct.combina.arata_cele_p1", { p1: r.nr_combinatii })}
        </button>
      </h3>
      <div className="sg-muted" style={{ marginBottom: 8, lineHeight: 1.55 }}>
        <p>
          {tx("struct.combina.nr_comb_sub", {
            f: r.nr_fundamentale, s: r.nr_seismice,
          })}
        </p>
        <p>
          <strong>{t("struct.combina.citeste_numele")}</strong>{" "}
          {tx("struct.combina.citeste_numele_lung")}{" "}
          <em>
            {t("struct.combina.explicatia_ultima_coloan")}
          </em>
        </p>
      </div>
      {deschis && (
        <div style={{ overflowX: "auto" }}>
          <table className="sg-table st-strat">
            <thead>
              <tr>
                <th>{t("struct.combina.combinatie")}</th>
                <th>
                  {t("struct.combina.th_grupa")}
                  <Info
                    titlu={t("struct.combina.face_grupa_combinatia")}
                    ce={t("struct.combina.fundamentala_seismica_in")}
                    deCe={t("struct.combina.verificarile_rezistenta")}
                  />
                </th>
                {cazuri.map((c) => {
                  const l = legenda.get(c);
                  return (
                    <th key={c} className="sg-mono">
                      {c}
                      {l && (
                        <Info
                          titlu={`${c} — ${l.nume}`}
                          ce={
                            <>
                              {l.unitar ?? l.descriere ?? l.nume}
                              {l.total_knm !== undefined && (
                                <> {t("struct.combina.pe_proiect_mt", { v: kN(l.total_knm) })}</>
                              )}
                              {l.total_kn !== undefined && (
                                <>
                                  {" "}{t("struct.combina.pe_proiect_total", {
                                    eticheta: l.total_eticheta ?? t("struct.caz.total_aplicat"),
                                    v: kN(l.total_kn),
                                  })}
                                </>
                              )}
                            </>
                          }
                          deCe={rolInCombinatii(l, r)}
                          parametri={[
                            ...(l.sursa
                              ? [{ simbol: t("struct.combina.p_sursa_datelor"), nume: l.sursa, sursa: t("struct.combina.p_proiectul_curent") }]
                              : []),
                            ...(l.normativ
                              ? [{ simbol: t("struct.combina.p_normativ"), nume: l.normativ, sursa: t("struct.combina.p_articolul_aplicat") }]
                              : []),
                          ]}
                        />
                      )}
                    </th>
                  );
                })}
                <th>{t("struct.combina.reprezinta_combinatia")}</th>
              </tr>
            </thead>
            <tbody>
              {r.combinatii.map((cb, i) => (
                <tr key={i}>
                  <td className="sg-mono" style={{ whiteSpace: "nowrap" }}>
                    <strong>{cb.nume}</strong>
                  </td>
                  <td className="sg-muted" title={DESCRIERE_GRUPA[cb.grupa] ? t(DESCRIERE_GRUPA[cb.grupa]) : ""}>
                    {cb.grupa}
                  </td>
                  {cazuri.map((c) => (
                    <td key={c} className="sg-mono">
                      {c in cb.coeficienti ? cb.coeficienti[c] : ""}
                    </td>
                  ))}
                  <td className="sg-muted" style={{ minWidth: 260 }}>
                    {descrieCombinatie(cb)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
